import { useState } from "react";
import axios from "axios";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Upload } from "lucide-react";
import { CONTACT } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InquiryForm = ({ source = "Website Inquiry", onDone }) => {
  const [f, setF] = useState({ full_name: "", best_contact: "", company_name: "", comment: "" });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name || !f.best_contact) { toast.error("Please add your name and the best way to reach you."); return; }
    if (!consent) { toast.error("Please provide consent to be contacted."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("source", source);
      fd.append("consent", "true");
      if (file) fd.append("document", file);
      const res = await axios.post(`${API}/freight-inquiry`, fd);
      setDone(true);
      toast.success(res.data.message);
      onDone && onDone();
    } catch {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="bg-oyf-grey rounded-lg p-8 text-center" data-testid="inquiry-success">
      <CheckCircle2 className="mx-auto text-oyf-yellow" size={44} />
      <p className="mt-4 font-display font-bold text-oyf-black text-lg leading-snug">
        Thank you. Your inquiry has been received. A One York Freight representative will reach out to you shortly.
      </p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      <p className="text-sm text-oyf-medgrey">
        Share a few details and a One York Freight representative will reach out to you shortly to discuss your shipment.
      </p>
      <div>
        <Label className="text-oyf-black font-semibold text-sm">Full name *</Label>
        <Input required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1.5 text-base" placeholder="Your name" data-testid="inq-name" />
      </div>
      <div>
        <Label className="text-oyf-black font-semibold text-sm">Best way to contact you *</Label>
        <Input required value={f.best_contact} onChange={(e) => set("best_contact", e.target.value)} className="mt-1.5 text-base" placeholder="Phone number or email address" data-testid="inq-contact" />
      </div>
      <div>
        <Label className="text-oyf-black font-semibold text-sm">Company name</Label>
        <Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} className="mt-1.5 text-base" placeholder="Your company (optional)" data-testid="inq-company" />
      </div>
      <div>
        <Label className="text-oyf-black font-semibold text-sm">How can we help?</Label>
        <Textarea rows={4} value={f.comment} onChange={(e) => set("comment", e.target.value)} className="mt-1.5 text-base" placeholder="Tell us about your shipment — route, timing, size, or any questions you have." data-testid="inq-comment" />
      </div>
      <div>
        <Label className="text-oyf-black font-semibold text-sm">Attach an image or document (optional)</Label>
        <label className="mt-1.5 flex items-center gap-3 border-2 border-dashed border-oyf-grey rounded-lg p-4 cursor-pointer hover:border-oyf-yellow transition-colors" data-testid="inq-file-label">
          <Upload size={20} className="text-oyf-medgrey" />
          <span className="text-sm text-oyf-medgrey">{file ? file.name : "Choose a file (PDF, image)"}</span>
          <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="inq-file" />
        </label>
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="inq-consent" />
        <span className="text-sm text-oyf-medgrey">I consent to being contacted by One York Freight regarding this inquiry.</span>
      </label>
      <button type="submit" disabled={loading} className="w-full bg-oyf-yellow text-oyf-black font-bold py-4 rounded-md hover:-translate-y-0.5 hover:shadow-[5px_5px_0_0_#202124] transition-transform duration-200 disabled:opacity-60" data-testid="inq-submit">
        {loading ? "Sending..." : "Send Inquiry"}
      </button>
    </form>
  );
};
